import { useEffect, useMemo, useState } from "react";

import { type ActivityRow, type ApiDeps, fetchActivity } from "../lib/api";
import {
  ACTIVITY_TYPES,
  ESTADO_LABEL_KEY,
  ESTADO_VALUES,
  type ActivityType,
  type Estado,
} from "../lib/activity";
import { t, tn, type Language } from "../lib/i18n";
import { presetToSince, type RangePreset } from "../lib/range";
import { ActivityRowItem } from "./ActivityRowItem";

interface AtividadeProps {
  deps: ApiDeps;
  language: Language;
  /** The global RangePicker preset — the Atividade feed is range-windowed
   * by its `since` lower bound (§13.2). */
  rangePreset: RangePreset;
  /** Shared refresh signal — bump to refetch from the first page. Defaults
   * to 0 so the component renders standalone (e.g. in unit tests). */
  reloadKey?: number;
}

const PAGE_LIMIT = 50;

type FeedState =
  | { kind: "loading" }
  | { kind: "loaded"; rows: ActivityRow[]; nextCursor: string | null }
  | { kind: "error" };

/** Accumulate the distinct `type`s seen across loaded rows. Drives the
 * "hide Segurança when the window has zero security events" rule (§13.3) —
 * seguranca enters the set only via an unfiltered (Todos) load surfacing a
 * security row, which is exactly the condition the spec gates on. */
function mergeTypes(
  prev: ReadonlySet<string>,
  rows: ActivityRow[],
): ReadonlySet<string> {
  if (rows.length === 0) return prev;
  const next = new Set(prev);
  for (const row of rows) next.add(row.type);
  return next;
}

/** The §13 Atividade tab: a range-windowed, type/estado-filterable,
 * cursor-paginated feed of what the agent did. `Tipo` is a multi-select
 * chip set (default Todos = no chips), `Estado` a tri-state, and
 * "Carregar mais" advances the opaque keyset cursor.
 *
 * The SPA sends type *keys* only (never an action list) — constraint ②;
 * the rows render through {@link ActivityRowItem} (operations-grade
 * sentences, never raw verbs — constraint ④). Universal — no tenant copy. */
export function Atividade({
  deps,
  language,
  rangePreset,
  reloadKey = 0,
}: AtividadeProps): JSX.Element {
  const [selectedTypes, setSelectedTypes] = useState<ReadonlySet<ActivityType>>(
    new Set(),
  );
  const [estado, setEstado] = useState<Estado>("all");
  const [state, setState] = useState<FeedState>({ kind: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);
  // Types observed in this window (sticky across filter changes; reset when
  // the range/refresh changes). A Set isn't a stable effect dependency, so
  // the fetch effect keys on `typesKey` (a sorted, joined primitive).
  const [typesSeen, setTypesSeen] = useState<ReadonlySet<string>>(new Set());
  const typesKey = useMemo(
    () => [...selectedTypes].sort().join(","),
    [selectedTypes],
  );

  // Forget the per-window observed-types memory when the window (or refresh)
  // changes — runs before the first-page fetch below merges the new window's
  // rows in.
  useEffect(() => {
    setTypesSeen(new Set());
  }, [rangePreset, reloadKey]);

  // First-page fetch — refires on any filter / range / refresh change.
  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    fetchActivity(deps, {
      types: typesKey ? typesKey.split(",") : [],
      estado,
      since: presetToSince(rangePreset),
      limit: PAGE_LIMIT,
    })
      .then((page) => {
        if (cancelled) return;
        setState({
          kind: "loaded",
          rows: page.items,
          nextCursor: page.next_cursor,
        });
        setTypesSeen((prev) => mergeTypes(prev, page.items));
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [deps.backendUrl, deps.getAuthToken, typesKey, estado, rangePreset, reloadKey]);

  const loadMore = (): void => {
    if (state.kind !== "loaded" || state.nextCursor === null || loadingMore) {
      return;
    }
    setLoadingMore(true);
    fetchActivity(deps, {
      types: typesKey ? typesKey.split(",") : [],
      estado,
      since: presetToSince(rangePreset),
      limit: PAGE_LIMIT,
      cursor: state.nextCursor,
    })
      .then((page) => {
        setState((prev) =>
          prev.kind === "loaded"
            ? {
                kind: "loaded",
                rows: [...prev.rows, ...page.items],
                nextCursor: page.next_cursor,
              }
            : prev,
        );
        setTypesSeen((prev) => mergeTypes(prev, page.items));
      })
      .catch(() => {
        /* keep the rows already shown; "Carregar mais" can be retried */
      })
      .finally(() => setLoadingMore(false));
  };

  const toggleType = (key: ActivityType): void => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Segurança chip hides when the window has surfaced no security events
  // (§13.3) — but never hide it while it's the active filter.
  const securityVisible =
    typesSeen.has("seguranca") || selectedTypes.has("seguranca");
  const visibleTypes = ACTIVITY_TYPES.filter(
    (tp) => tp.key !== "seguranca" || securityVisible,
  );

  return (
    <section
      aria-label={t("atividade.feed_label", language)}
      className="rounded-[2px] border border-kwanza-divider bg-kwanza-card p-4 shadow-sm"
    >
      <div className="mb-3 flex flex-col gap-2">
        <div
          role="group"
          aria-label={t("atividade.tipo_label", language)}
          className="flex flex-wrap items-center gap-1"
          data-testid="tipo-filter"
        >
          <span className="mr-1 text-xs font-medium text-kwanza-inkSoft">
            {t("atividade.tipo_label", language)}:
          </span>
          {visibleTypes.map(({ key, labelKey }) => {
            const active = selectedTypes.has(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleType(key)}
                aria-pressed={active}
                data-testid={`tipo-${key}`}
                className={
                  "rounded px-2.5 py-1 text-xs font-medium " +
                  (active
                    ? "bg-kwanza-accent font-bold text-kwanza-onAccent"
                    : "bg-kwanza-surface text-kwanza-inkSoft hover:text-kwanza-ink")
                }
              >
                {t(labelKey, language)}
              </button>
            );
          })}
        </div>
        <div
          role="group"
          aria-label={t("atividade.estado_label", language)}
          className="flex flex-wrap items-center gap-1"
          data-testid="estado-filter"
        >
          <span className="mr-1 text-xs font-medium text-kwanza-inkSoft">
            {t("atividade.estado_label", language)}:
          </span>
          {ESTADO_VALUES.map((value) => {
            const active = estado === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setEstado(value)}
                aria-pressed={active}
                data-testid={`estado-${value}`}
                className={
                  "rounded px-2.5 py-1 text-xs font-medium " +
                  (active
                    ? "bg-kwanza-accent font-bold text-kwanza-onAccent"
                    : "bg-kwanza-surface text-kwanza-inkSoft hover:text-kwanza-ink")
                }
              >
                {t(ESTADO_LABEL_KEY[value], language)}
              </button>
            );
          })}
        </div>
      </div>

      {state.kind === "loading" ? (
        <ul aria-busy="true" data-testid="atividade-skeleton">
          {[0, 1, 2, 3, 4].map((i) => (
            <li
              key={i}
              className="mb-2 h-6 animate-pulse rounded bg-kwanza-divider"
            />
          ))}
        </ul>
      ) : state.kind === "error" ? (
        <p className="text-sm text-kwanza-alert" data-testid="atividade-error">
          {t("error.fetch_failed.body", language)}
        </p>
      ) : state.rows.length === 0 ? (
        <p className="text-sm text-kwanza-inkSoft" data-testid="atividade-empty">
          {t("atividade.empty", language)}
        </p>
      ) : (
        <>
          <ul
            className="divide-y divide-kwanza-divider"
            data-testid="atividade-feed"
          >
            {state.rows.map((row) => (
              <ActivityRowItem
                key={`${row.timestamp}-${row.correlation_id}`}
                row={row}
                language={language}
              />
            ))}
          </ul>
          <div className="mt-3 flex items-center justify-between gap-3">
            {/* P24-safe loaded-count — "eventos", never an outcome-blind
             * "things done". aria-live announces the new total when
             * "Carregar mais" appends a page. */}
            <span
              className="text-xs text-kwanza-inkSoft"
              data-testid="atividade-count"
              role="status"
              aria-live="polite"
            >
              {tn(
                state.rows.length === 1
                  ? "atividade.count.one"
                  : "atividade.count.other",
                { n: state.rows.length },
                language,
              )}
            </span>
            {state.nextCursor !== null ? (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="atividade-load-more"
                className="rounded border border-kwanza-divider bg-kwanza-surface px-3 py-1 text-sm font-medium text-kwanza-ink hover:bg-kwanza-accentSoft disabled:opacity-60"
              >
                {loadingMore
                  ? t("atividade.loading_more", language)
                  : t("atividade.load_more", language)}
              </button>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}
